import {
  getSessionToken,
  useDescope,
  useSession,
  useUser,
} from '@descope/react-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { clearLastVisitedPath } from '@/lib/lastVisitedPath';
import { clearPersistedInviteToken } from '@/lib/inviteTokenPersistence';
import { useScopeChangeAction } from './useScopeChangeHooks';

export const API_BASE =
  import.meta.env.VITE_API_BASE || 'http://localhost:13722';

type AuthUser = { name: string; email: string };

export function useAuth() {
  const { logout } = useDescope();
  const { isAuthenticated, isSessionLoading } = useSession();
  const { user, isUserLoading } = useUser();
  const { changeScopeTo } = useScopeChangeAction();

  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [apiKey, setApiKey] = useState<string>('');
  const [showSignIn, setShowSignIn] = useState(false);
  const [flowId, setFlowId] = useState('');
  const [loading, setLoading] = useState(false);
  // after state hooks, before useEffect/callbacks
  const normalizedUser: AuthUser | null = useMemo(() => {
    const raw = (user as { email?: string; name?: string } | null) || null;
    const email = String(raw?.email || '');
    const name = String(raw?.name || (email ? email.split('@')[0] : 'User'));
    return raw ? { email, name } : null;
  }, [user]);

  const checkExistingKey = useCallback(async () => {
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/account/keys/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(normalizedUser?.email
            ? { 'X-User-Email': normalizedUser.email }
            : {}),
          ...(normalizedUser?.name
            ? { 'X-User-Name': normalizedUser.name }
            : {}),
        },
      });
      const data = await res.json();
      setHasExistingKey(data.hasKey);
    } catch (e) {
      console.error('Failed to check key status:', e);
    }
  }, [normalizedUser]);

  useEffect(() => {
    if (isAuthenticated && !isSessionLoading && !isUserLoading) {
      checkExistingKey();
    }
  }, [isAuthenticated, isSessionLoading, isUserLoading, checkExistingKey]);

  const generateApiKey = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/account/keys/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(normalizedUser?.email
            ? { 'X-User-Email': normalizedUser.email }
            : {}),
          ...(normalizedUser?.name
            ? { 'X-User-Name': normalizedUser.name }
            : {}),
        },
      });
      if (!res.ok) throw new Error('Failed to generate API key');
      const data = await res.json();
      setApiKey(data.apiKey);
      setHasExistingKey(true);
    } catch (e) {
      console.error('Failed to generate API key:', e);
      alert('Failed to generate API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const signIn = (flow: string) => {
    setFlowId(flow);
    setShowSignIn(true);
  };

  const signOut = async () => {
    // Log logout event before actually logging out
    try {
      await apiClient.post('/account/logout');
    } catch (err) {
      console.warn('Failed to log logout event:', err);
      // Continue with logout even if audit logging fails
    }
    clearLastVisitedPath();
    // Drop any pending invite token so a shared browser doesn't surface the
    // prior user's mandatory join-org modal to the next (different) user.
    clearPersistedInviteToken();
    logout();
    setHasExistingKey(false);
    setApiKey('');
  };

  const guardedSignout = () => {
    changeScopeTo({
      id: 'logout',
      run: signOut,
    });
  };

  const onSuccess = () => setShowSignIn(false);
  const onError = (err: unknown) => console.error('Auth error:', err);
  const cancelSignIn = () => setShowSignIn(false);

  return {
    isAuthenticated,
    isSessionLoading,
    isUserLoading,
    user: normalizedUser,

    hasExistingKey,
    apiKey,
    loading,
    generateApiKey,

    showSignIn,
    flowId,
    signIn,
    signOut: guardedSignout,
    onSuccess,
    onError,
    cancelSignIn,
  };
}
